/**
 * The dark card on the brand backdrop that a pre-session page wears.
 *
 * Extracted from `LoginPage` when the refused-sign-in page needed the same
 * frame (#1951): someone reading why they cannot get in should not feel they
 * left the product to read it. The white-label brand is painted here too, so a
 * tenant's own logo and colours reach every page in the set without each one
 * repeating the wiring.
 */

import { useMemo, type ReactNode } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useBranding } from '@/hooks/useBranding';
import { buildThemeFromBranding } from '@/utils/themeBuilder';
import osBuddyLogo from '@/assets/os-buddy-logo-light.svg';

export function AuthCard({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { branding: brandingConfig } = useBranding();
  const companyName = brandingConfig?.companyName;
  const tenantLogo = brandingConfig?.logo;

  // The card is always dark (Figma "Dark 2" navy), independent of the app
  // theme mode. Tenant primary/secondary still flow through, so buttons
  // respect white-label overrides.
  const cardTheme = useMemo(
    () => buildThemeFromBranding(brandingConfig, 'dark'),
    [brandingConfig]
  );

  // Tenant logo wins (white-label); fall back to the OS Buddy light wordmark,
  // which reads well on the dark card.
  const logoSrc = tenantLogo?.darkUrl ?? tenantLogo?.url ?? osBuddyLogo;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        // Full-screen brand backdrop: the exploded-machinery photo from Figma
        // ("BG IMAGE", node 84:89), served from public/. The light grey
        // fallback colour keeps the dark card contrasting if the image fails
        // to load.
        backgroundColor: '#D7DBE0', // design-allow-hex: fixed brand backdrop fallback behind login photo, not a palette color
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Marketing wordmark, top-left (brand proper noun) */}
      <Typography
        component="span"
        aria-hidden
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 32 },
          color: 'rgba(255,255,255,0.92)',
          fontWeight: 600,
          letterSpacing: '0.18em',
          fontSize: { xs: '0.95rem', sm: '1.1rem' },
          textTransform: 'uppercase',
          userSelect: 'none',
        }}
      >
        {t('login.vendorWordmark')}
      </Typography>

      <ThemeProvider theme={cardTheme}>
        <Card
          elevation={0}
          sx={{
            maxWidth: 400,
            width: '100%',
            bgcolor: 'background.paper', // Dark 2 #151A23 (Figma login card)
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Box
                component="img"
                src={logoSrc}
                alt={companyName ?? t('login.logoAlt')}
                sx={{ height: 56, maxWidth: '100%', objectFit: 'contain' }}
              />
            </Box>

            {children}
          </CardContent>
        </Card>
      </ThemeProvider>
    </Box>
  );
}
